import type { AmplitudeProductEventName } from './productEvents';
import { AmplitudeProductEvent } from './productEvents';

export type AmplitudeProductEventPlatform = 'shared' | 'native' | 'web';

export interface AmplitudeProductEventMeta {
  /** Target Amplitude event type (bucket b). */
  name: AmplitudeProductEventName;
  /** Where the event is instrumented today / planned. */
  platform: AmplitudeProductEventPlatform;
  /** Prior UserAction action or top-level name in code before migration. */
  legacyNames: readonly string[];
}

/**
 * Catalog metadata for every bucket **(b)** product event (canonical `name`, `platform`, migration `legacyNames`).
 *
 * Runtime tracking should use {@link AmplitudeProductEvent} + {@link AmplitudeProductEventPayloadMap}
 * from `productEvents.ts` / `productEventPayloads.ts`. This array is for **auditing**, **docs**, and **tests**
 * that the taxonomy stays complete as native/web migrate off `UserAction`.
 *
 * @example Vitest — registry stays in sync with `AmplitudeProductEvent` constants
 * ```ts
 * import { AMPLITUDE_PRODUCT_EVENT_REGISTRY, AmplitudeProductEvent } from '@cfa/pathway-fe-shared';
 *
 * it('every product constant has registry metadata', () => {
 *   const fromRegistry = new Set(AMPLITUDE_PRODUCT_EVENT_REGISTRY.map(e => e.name));
 *   for (const name of Object.values(AmplitudeProductEvent)) {
 *     expect(fromRegistry.has(name)).toBe(true);
 *   }
 * });
 * ```
 *
 * @example Vitest — legacy `action` strings map to exactly one target event (migration audit helper)
 * ```ts
 * import { AMPLITUDE_PRODUCT_EVENT_REGISTRY } from '@cfa/pathway-fe-shared';
 *
 * const legacyToCanonical = new Map<string, string>();
 * for (const entry of AMPLITUDE_PRODUCT_EVENT_REGISTRY) {
 *   for (const legacy of entry.legacyNames) {
 *     legacyToCanonical.set(legacy, entry.name);
 *   }
 * }
 *
 * it('maps old ResourceView to resource_view', () => {
 *   expect(legacyToCanonical.get('ResourceView')).toBe('resource_view');
 * });
 *
 * // After codemod: scan app source for remaining legacy strings and fail if any
 * // still appear outside allowlisted test fixtures:
 * // expect(remainingLegacyActions).toEqual([]);
 * ```
 *
 * @example Vitest — platform scope (native must not require web-only events in shared helpers)
 * ```ts
 * import { AMPLITUDE_PRODUCT_EVENT_REGISTRY } from '@cfa/pathway-fe-shared';
 *
 * const webOnly = AMPLITUDE_PRODUCT_EVENT_REGISTRY
 *   .filter(e => e.platform === 'web')
 *   .map(e => e.name);
 *
 * it('documents web-only funnel events', () => {
 *   expect(webOnly).toContain('quiz_created');
 * });
 * ```
 *
 * @see `tests/amplitude/productEvents.test.ts` for checks enforced in this package today.
 */
export const AMPLITUDE_PRODUCT_EVENT_REGISTRY: readonly AmplitudeProductEventMeta[] =
  [
    {
      name: AmplitudeProductEvent.LOGIN_SUCCESS,
      platform: 'shared',
      legacyNames: ['LoginSuccess'],
    },
    {
      name: AmplitudeProductEvent.COLOR_MODE,
      platform: 'shared',
      legacyNames: ['color_mode'],
    },
    {
      name: AmplitudeProductEvent.CATEGORY_CLICK,
      platform: 'shared',
      legacyNames: ['category_click'],
    },
    {
      name: AmplitudeProductEvent.SUBCATEGORY_CLICK,
      platform: 'shared',
      legacyNames: ['subcategory_click'],
    },
    {
      name: AmplitudeProductEvent.SEARCH_SUGGESTION_CLICKED,
      platform: 'shared',
      legacyNames: ['searchSuggestionClicked'],
    },
    {
      name: AmplitudeProductEvent.SEARCH_RESULT,
      platform: 'shared',
      legacyNames: ['search_result'],
    },
    {
      name: AmplitudeProductEvent.SEARCH_RESULT_CLICK,
      platform: 'shared',
      legacyNames: ['search_result_click', 'searchClick'],
    },
    {
      name: AmplitudeProductEvent.SEARCH_TIME_SPENT,
      platform: 'shared',
      legacyNames: ['search_time_spent'],
    },
    {
      name: AmplitudeProductEvent.RESOURCE_VIEW,
      platform: 'shared',
      legacyNames: ['ResourceView'],
    },
    {
      name: AmplitudeProductEvent.CONTENT_ISSUE,
      platform: 'native',
      legacyNames: ['ContentIssue'],
    },
    {
      name: AmplitudeProductEvent.DOCUMENT_PRINT,
      platform: 'shared',
      legacyNames: ['document_print'],
    },
    {
      name: AmplitudeProductEvent.RESOURCE_PRINT,
      platform: 'shared',
      legacyNames: ['resource_print'],
    },
    {
      name: AmplitudeProductEvent.CERTIFICATE_PRINT,
      platform: 'shared',
      legacyNames: ['certificate_print'],
    },
    {
      name: AmplitudeProductEvent.REPORT_PRINT,
      platform: 'shared',
      legacyNames: ['report_print'],
    },
    {
      name: AmplitudeProductEvent.DOCUMENT_SHARE,
      platform: 'native',
      legacyNames: ['document_share'],
    },
    {
      name: AmplitudeProductEvent.DOCUMENT_SHARE_DISMISSED,
      platform: 'native',
      legacyNames: ['document_share_dismissed'],
    },
    {
      name: AmplitudeProductEvent.DOCUMENT_SWITCH_LANGUAGE,
      platform: 'native',
      legacyNames: ['document_switch_language'],
    },
    {
      name: AmplitudeProductEvent.DOCUMENT_OPTIONS,
      platform: 'native',
      legacyNames: ['document_options'],
    },
    {
      name: AmplitudeProductEvent.DOCUMENT_OPTIONS_CANCEL,
      platform: 'native',
      legacyNames: ['document_options_cancel'],
    },
    {
      name: AmplitudeProductEvent.RESOURCE_SCREENSHOT,
      platform: 'native',
      legacyNames: ['resource_screenshot'],
    },
    {
      name: AmplitudeProductEvent.TEXT_COPY,
      platform: 'shared',
      legacyNames: ['text_copy'],
    },
    {
      name: AmplitudeProductEvent.TEXT_PASTE,
      platform: 'shared',
      legacyNames: ['text_paste'],
    },
    {
      name: AmplitudeProductEvent.TEXT_SELECTION,
      platform: 'shared',
      legacyNames: ['text_selection'],
    },
    {
      name: AmplitudeProductEvent.SMART_SEARCH_TOGGLE,
      platform: 'shared',
      legacyNames: ['smart_search_toggle'],
    },
    {
      name: AmplitudeProductEvent.SMART_SEARCH_EMBEDDED_REFERENCE_TAPPED,
      platform: 'native',
      legacyNames: ['SmartSearch.embeddedReferenceTapped'],
    },
    {
      name: AmplitudeProductEvent.SMART_SEARCH_REFERENCE_IMAGE_OPENED,
      platform: 'native',
      legacyNames: ['SmartSearch.referenceImageOpened'],
    },
    {
      name: AmplitudeProductEvent.SMART_SEARCH_REFERENCE_PREVIEW_CLOSED,
      platform: 'native',
      legacyNames: ['SmartSearch.referencePreviewClosed'],
    },
    {
      name: AmplitudeProductEvent.SMART_SEARCH_REFERENCE_SOURCE_CARD_TAPPED,
      platform: 'native',
      legacyNames: ['SmartSearch.referenceSourceCardTapped'],
    },
    {
      name: AmplitudeProductEvent.AGE_VERIFICATION_SCREEN_VIEWED,
      platform: 'native',
      legacyNames: ['age_verification_screen_viewed'],
    },
    {
      name: AmplitudeProductEvent.AGE_VERIFICATION_BLOCKED,
      platform: 'native',
      legacyNames: ['age_verification_blocked'],
    },
    {
      name: AmplitudeProductEvent.QUIZ_CREATED,
      platform: 'web',
      legacyNames: ['quiz_created'],
    },
    {
      name: AmplitudeProductEvent.QUIZ_UPDATED,
      platform: 'web',
      legacyNames: ['quiz_updated'],
    },
    {
      name: AmplitudeProductEvent.TRAINING_PLAN_CREATED,
      platform: 'web',
      legacyNames: ['training_plan_created'],
    },
    {
      name: AmplitudeProductEvent.TRAINING_TEMPLATE_COPIED,
      platform: 'web',
      legacyNames: ['training_template_copied'],
    },
    {
      name: AmplitudeProductEvent.TRANSLATE_ALL,
      platform: 'web',
      legacyNames: ['translate_all'],
    },
    {
      name: AmplitudeProductEvent.TRANSLATE_SINGLE,
      platform: 'web',
      legacyNames: ['translate_single'],
    },
    {
      name: AmplitudeProductEvent.PLAN_CREATE_TO_FIRST_ASSIGN_DURATION,
      platform: 'web',
      legacyNames: ['plan_create_to_first_assign_duration'],
    },
    {
      name: AmplitudeProductEvent.PLAN_EDIT_TIME_SPENT,
      platform: 'web',
      legacyNames: ['plan_edit_time_spent'],
    },
    {
      name: AmplitudeProductEvent.PLAN_ASSIGN_TIME_SPENT,
      platform: 'web',
      legacyNames: ['plan_assign_time_spent'],
    },
    {
      name: AmplitudeProductEvent.CHOOSE_TEAM_MEMBER_MODAL_DURATION,
      platform: 'web',
      legacyNames: ['choose_team_member_modal_duration'],
    },
    {
      name: AmplitudeProductEvent.PLAN_TASK_BOUNCE,
      platform: 'web',
      legacyNames: ['plan_task_bounce'],
    },
    {
      name: AmplitudeProductEvent.RESOURCE_NOT_FOUND,
      platform: 'web',
      legacyNames: ['resource_not_found'],
    },
    {
      name: AmplitudeProductEvent.TEAM_MEMBERS_ASSIGNED,
      platform: 'web',
      legacyNames: ['team_members_assigned'],
    },
    {
      name: AmplitudeProductEvent.AUTO_ASSIGN_ENABLED,
      platform: 'web',
      legacyNames: ['auto_assign_enabled'],
    },
    {
      name: AmplitudeProductEvent.PLAN_DESCRIPTION_SAVED,
      platform: 'web',
      legacyNames: ['plan_description_saved'],
    },
    {
      name: AmplitudeProductEvent.ARCHIVE_PLAN_CLICKED,
      platform: 'web',
      legacyNames: ['archive_plan_clicked'],
    },
    {
      name: AmplitudeProductEvent.ACTIVATE_PLAN_CLICKED,
      platform: 'web',
      legacyNames: ['activate_plan_clicked'],
    },
    {
      name: AmplitudeProductEvent.SHOW_TRAIN_TOGETHER_MODE,
      platform: 'web',
      legacyNames: ['showTrainTogetherMode'],
    },
  ];
